import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { initSentry } from './lib/sentry'
import './i18n'
import './index.css'
import App from './App.tsx'

initSentry()

// After a deploy, browsers holding a stale index.html may try to
// preload asset chunks at hashes that no longer exist. Detect those
// errors and force-reload once with a cache-busting param so the user
// gets the fresh HTML. Guarded by sessionStorage to avoid a reload
// loop if the network is genuinely broken.
const RELOAD_FLAG = '__chunk_reload__'
function looksLikeChunkLoadError(message: string): boolean {
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Unable to preload (?:CSS|module|script)/i.test(message) ||
    /Loading chunk \d+ failed/i.test(message) ||
    /Importing a module script failed/i.test(message)
  )
}
function handleChunkError(message: string) {
  if (sessionStorage.getItem(RELOAD_FLAG)) return
  if (!looksLikeChunkLoadError(message)) return
  sessionStorage.setItem(RELOAD_FLAG, '1')
  const url = new URL(window.location.href)
  url.searchParams.set('_r', String(Date.now()))
  window.location.replace(url.toString())
}
window.addEventListener('error', (e) => {
  handleChunkError(e?.message ?? '')
})
window.addEventListener('unhandledrejection', (e) => {
  const reason = e?.reason
  const msg = typeof reason === 'string' ? reason : (reason?.message ?? '')
  handleChunkError(msg)
})
// Clear the flag once the page mounts cleanly.
window.addEventListener('load', () => {
  setTimeout(() => sessionStorage.removeItem(RELOAD_FLAG), 5000)
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)
